<template>
  <div>
    <!-- 独立登录页：直接访问 /login 时仍然可用（正常流程走弹窗） -->
    <router-view v-if="$route.path === '/login'" />
    <!-- 主布局 -->
    <el-container v-else class="app-shell">
      <!-- 桌面端侧栏：窄图标栏（图标在上、文字在下），移动端隐藏改用抽屉 -->
      <el-aside v-if="!isMobile" width="76px" class="app-aside">
        <div class="logo">
          <img class="logo-img" :src="logoImg" alt="創視魔方" />
        </div>
        <el-menu
          :default-active="activeMenu"
          router
          class="side-menu"
        >
          <el-menu-item index="/home">
            <i class="el-icon-s-home"></i>
            <span slot="title">首頁</span>
          </el-menu-item>
          <el-menu-item index="/agent">
            <i class="el-icon-chat-dot-round"></i>
            <span slot="title">Agent</span>
          </el-menu-item>
          <el-menu-item index="/workbench">
            <i class="el-icon-picture-outline"></i>
            <span slot="title">AI 圖片</span>
          </el-menu-item>
          <el-menu-item index="/video">
            <i class="el-icon-video-camera"></i>
            <span slot="title">AI 影片</span>
          </el-menu-item>
          <el-menu-item index="/text">
            <i class="el-icon-edit-outline"></i>
            <span slot="title">AI 生文</span>
          </el-menu-item>
          <el-menu-item index="/materials">
            <i class="el-icon-folder-opened"></i>
            <span slot="title">素材庫</span>
          </el-menu-item>
          <el-menu-item index="/recharge">
            <i class="el-icon-wallet"></i>
            <span slot="title">儲值</span>
          </el-menu-item>
        </el-menu>
      </el-aside>

      <el-container class="right-wrap">
        <el-header class="app-header">
          <div class="header-left">
            <!-- 移动端汉堡按钮 -->
            <button v-if="isMobile" class="menu-btn" @click="drawer = true">
              <i class="el-icon-menu"></i>
            </button>
          </div>
          <div class="user-bar">
            <!-- 未登录：只给一个入口，平台内容照常可看。
                 按钮上把「登入」和「註冊」都写出来 —— 只写「登入」的话，
                 新用户会以为这里没有注册入口（注册是在同一个弹窗里切页签）。 -->
            <el-button v-if="!loggedIn" size="small" type="primary" @click="openLogin">
              <i class="el-icon-user"></i> 登入 / 註冊
            </el-button>
            <!-- 用 el-popover 而不是 el-dropdown：后者把结构锁死成 ul/li，
                 头部卡片只能靠 <li> 硬塞，分组线还要跟 Element 的 border-top/:before 打架。
                 popover 里可以写任意结构，样式完全可控。 -->
            <el-popover
              v-else
              v-model="userPopVisible"
              placement="bottom-end"
              :trigger="isTouch ? 'click' : 'hover'"
              popper-class="user-pop"
              :width="288"
              :offset="10"
              :open-delay="80"
              :close-delay="140"
              @show="loadPoints"
            >
              <!-- 头部：饱和深海蓝渐变 + 玻璃头像 + 大号积分 -->
              <div class="up-head">
                <div class="up-id">
                  <span class="up-ava">{{ avatarChar }}</span>
                  <span class="up-name" :title="displayName">{{ displayName }}</span>
                </div>
                <div class="up-balance" title="去儲值" @click="onUserCmd('recharge')">
                  <i class="el-icon-coin"></i>
                  <b>{{ pointsText }}</b>
                  <em>積分</em>
                </div>
              </div>

              <div class="up-list">
                <div class="up-it" @click="onUserCmd('jobs')">
                  <i class="up-ic el-icon-tickets"></i><span class="up-lb">任務中心</span>
                </div>
                <div class="up-it up-dv" @click="onUserCmd('profile')">
                  <i class="up-ic el-icon-user"></i><span class="up-lb">個人中心</span>
                </div>
                <div class="up-it up-dv" @click="onUserCmd('recharge')">
                  <i class="up-ic el-icon-wallet"></i><span class="up-lb">儲值</span>
                </div>
                <!-- 聯繫客服：放在登出上方（登出是危险操作，永远留在最后一项） -->
                <div class="up-it up-dv" @click="onUserCmd('support')">
                  <i class="up-ic el-icon-service"></i><span class="up-lb">聯繫客服</span>
                </div>
                <div class="up-it up-dv up-danger" @click="onUserCmd('logout')">
                  <i class="up-ic el-icon-switch-button"></i><span class="up-lb">登出</span>
                </div>
              </div>

              <span slot="reference" class="user-name">
                <span class="avatar-sm">{{ avatarChar }}</span>
                <span class="uname">{{ displayName }}</span>
              </span>
            </el-popover>
          </div>
        </el-header>
        <el-main class="app-main">
          <keep-alive include="Workbench,VideoWorkbench">
            <router-view />
          </keep-alive>
        </el-main>
      </el-container>

      <!-- 移动端抽屉菜单（完整文字，便于点击） -->
      <el-drawer title="創視魔方" :visible.sync="drawer" direction="ltr" size="240px" class="app-drawer">
        <div class="logo-row">
          <img class="logo-img" :src="logoImg" alt="創視魔方" />
          <span class="logo-text">創視魔方</span>
        </div>
        <el-menu
          :default-active="activeMenu"
          router
          class="side-menu drawer-menu"
          @select="drawer = false"
        >
          <el-menu-item index="/home"><i class="el-icon-s-home"></i><span>首頁</span></el-menu-item>
          <el-menu-item index="/agent"><i class="el-icon-chat-dot-round"></i><span>Agent</span></el-menu-item>
          <el-menu-item index="/workbench"><i class="el-icon-picture-outline"></i><span>AI 圖片</span></el-menu-item>
          <el-menu-item index="/video"><i class="el-icon-video-camera"></i><span>AI 影片</span></el-menu-item>
          <el-menu-item index="/text"><i class="el-icon-edit-outline"></i><span>AI 生文</span></el-menu-item>
          <el-menu-item index="/materials"><i class="el-icon-folder-opened"></i><span>素材庫</span></el-menu-item>
          <el-menu-item index="/recharge"><i class="el-icon-wallet"></i><span>儲值</span></el-menu-item>
        </el-menu>
      </el-drawer>

      <!-- 聯繫客服：LINE 二維碼（入口在右上角用戶彈層「登出」上方） -->
      <el-dialog
        :visible.sync="supportVisible"
        title="聯繫客服"
        width="330px"
        custom-class="support-dialog"
        append-to-body
      >
        <div class="cs-body">
          <p class="cs-tip">加入 LINE 好友<br />由客服為你處理</p>
          <!-- 二維碼本身可點：手機上點一下就直接開 LINE，不必先截圖再掃 -->
          <a
            class="cs-qr"
            :href="lineUrl"
            target="_blank"
            rel="noopener"
            title="點擊前往 LINE 聯繫客服"
            aria-label="點擊前往 LINE 聯繫客服"
          >
            <img :src="lineQrImg" alt="LINE 客服 QR Code" />
          </a>
          <p class="cs-hint">點擊或掃描 QR Code 聯繫客服</p>
        </div>
      </el-dialog>

      <!-- 该登录时才弹的登录框 -->
      <login-dialog />
      <!-- 账号相关的四个页面：就地弹窗，不跳页 -->
      <account-dialogs ref="acctDlgs" />
    </el-container>
  </div>
</template>

<script>
import LoginDialog from './components/LoginDialog.vue'
import AccountDialogs from './components/AccountDialogs.vue'
import logoImg from './assets/img/logo.png'
import lineQrImg from './assets/img/line-qr.png'
import { fetchBalance } from './api/user'
import { inviteCodeFromLocation, clearInviteFromLocation } from './utils/invite'

// 客服 LINE 好友連結（與彈窗裡的二維碼指向同一個官方帳號）
const LINE_URL = 'https://lin.ee/XoJgFUv'

export default {
  name: 'App',
  components: { LoginDialog, AccountDialogs },
  data() {
    return {
      logoImg,
      lineQrImg,
      lineUrl: LINE_URL,
      // 聯繫客服彈窗（LINE 二維碼）
      supportVisible: false,
      isMobile: false,
      // 触屏设备上 hover 触发不可靠（tap 展开后不容易收起），退回点击展开
      isTouch: typeof window !== 'undefined' && 'ontouchstart' in window,
      drawer: false,
      // 账号弹层的显隐（由 hover 触发，但点菜单项后要能主动收起 ——
      // 否则鼠标还停在用户名上，弹层会和刚打开的页面弹窗叠在一起）
      userPopVisible: false,
      // 当前剩余积分（下拉里展示）；打开下拉时刷新一次，保证刚生成完也准
      points: null
    }
  },
  computed: {
    /**
     * 菜单高亮项：子路由要归到父项上，否则切 tab / 进竞品参考后菜单就不亮了。
     * （el-menu 的 default-active 是按 path 精确匹配的，/text/blog 不等于 /text）
     */
    activeMenu() {
      const p = this.$route.path || ''
      if (p.indexOf('/text') === 0) return '/text'
      if (p.indexOf('/workbench') === 0) return '/workbench'
      if (p.indexOf('/video') === 0) return '/video'
      return p
    },
    user() {
      return this.$store.state.user
    },
    loggedIn() {
      return this.$store.getters.isLoggedIn
    },
    /** 剩余积分展示文本：还没拉到用 —，拉到 0 也要显示 0（支持小数） */
    pointsText() {
      return this.points === null || this.points === undefined ? '—' : this.$fmtPoints(this.points)
    },
    /** 头像里的首字母 */
    avatarChar() {
      return String(this.displayName).charAt(0).toUpperCase()
    },
    /** 显示名：优先昵称，其次用户名；未登录时给提示文字 */
    displayName() {
      const u = this.user
      if (!u) return '未登入'
      if (u.nickname) return u.nickname
      if (u.username) return u.username
      return '已登入'
    }
  },
  created() {
    this.$store.dispatch('loadDefaults')
    this.$store.dispatch('initAuth')
    this.$store.dispatch('loadModules')
    this.mediaQueryHandler()
    window.addEventListener('resize', this.mediaQueryHandler)
  },
  mounted() {
    // ★放在 mounted 而不是 created：邀请码要落到 <login-dialog> 的表单里，
    //   子组件得先挂载 —— 在 created 里 dispatch 的话，子组件的 watch 还没建立，
    //   弹框会出来但停在登入页（2026-10-20 实测踩到）。
    this.applyInviteFromUrl()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.mediaQueryHandler)
  },
  methods: {
    mediaQueryHandler() {
      this.isMobile = window.innerWidth < 768
      if (!this.isMobile) this.drawer = false
    },
    /**
     * 从邀请链接进来（经销商名片上的 `?invite=CODE`）：直接弹注册页并预填邀请码。
     *
     * 先把参数从 URL 上摘掉再弹 —— 否则登录成功后 redirect 回同一路径会再弹一次，
     * 刷新页面也会反复弹。邀请码这时已经落到 store → 注册表单里了。
     * 已登录的用户只是被摘掉参数，不打扰。
     */
    applyInviteFromUrl() {
      const code = inviteCodeFromLocation(this.$route)
      if (!code) {
        return
      }
      clearInviteFromLocation(this.$router, this.$route)
      if (this.loggedIn) {
        return
      }
      this.$store.dispatch('openLoginWithInvite', { code, redirect: this.$route.path })
    },
    /** 打开登录弹窗（未登录时点「登录」入口） */
    openLogin() {
      this.drawer = false
      this.$store.dispatch('openLogin', this.$route.fullPath)
    },
    /** 用户下拉展开时刷新积分（刚生成完能立刻看到最新余额） */
    /** 拉当前剩余积分；未登录/失败就保持 —，不打扰 */
    async loadPoints() {
      if (!this.loggedIn) {
        this.points = null
        return
      }
      try {
        const d = await fetchBalance()
        this.points = d && d.points !== undefined && d.points !== null ? d.points : null
      } catch (e) {
        this.points = null
      }
    },
    async onUserCmd(cmd) {
      // 点击后先收起账号弹层：hover 触发的弹层不会因为点击自动关闭，
      // 鼠标仍停在用户名上时它会一直开着，和下面打开的页面弹窗叠在一起。
      this.userPopVisible = false
      // 任務中心 / 個人中心：就地弹窗，不跳页 ——
      // 跳页会把工作台的状态丢掉（正在生成的图、已勾选待生成的竞品图）。
      // 路由本身仍保留（/jobs 等老链接不会 404），只是从菜单进来走弹窗。
      // 「儲值」跳独立页：充值要走「下单 → 跳转蓝新付款页 → 回调」，
      // 是个独立流程，弹窗里放不下（而且要离开本站）。
      // 「範本管理」已移到**管理端**（管理端侧边栏「提示詞範本」）：提示词是平台级
      // 配置，用户改了会影响所有人的出图，不该开放给用户端。客户端入口已移除。
      if (cmd === 'jobs' || cmd === 'profile') {
        this.$refs.acctDlgs.open(cmd)
        return
      }
      if (cmd === 'recharge') {
        this.safePush('/recharge')
        return
      }
      if (cmd === 'support') {
        this.supportVisible = true
        return
      }
      if (cmd === 'logout') {
        await this.$store.dispatch('doLogout')
        this.$message.success('已登出')
        this.safePush('/home') // 退出后不再跳登录页，留在平台里
      }
    },
    safePush(path) {
      if (this.$route.path === path) {
        return
      }
      this.$router.push(path).catch(() => {})
    }
  }
}
</script>

<style>
/* ===== 主框架：清新简约（浅色底 + 白侧栏 + 深蓝点缀）=====
   这里只管布局骨架与自有类；Element 组件的外观统一由 styles/common.css 的设计令牌负责。 */
.app-shell {
  height: 100vh;
  /* 整个框架共用一块背景：顶部淡深蓝向下渐隐 + 右上一点深藏蓝。
     侧栏与内容区都透明，所以左右视觉上连成一体，中间没有分割线。 */
  background:
    linear-gradient(180deg, rgba(59, 130, 246, 0.06), rgba(59, 130, 246, 0) 340px),
    radial-gradient(900px 420px at 90% -8%, rgba(59, 130, 246, 0.1), transparent 62%);
  color: var(--c-text-1);
}
.right-wrap {
  background: transparent;
  overflow: hidden;
}

/* ------- 侧边栏：窄图标栏（图标在上、文字在下） ------- */
.app-aside {
  /* 透明、无右边框：和右侧共用同一块背景，视觉上不分区 */
  background: transparent;
  border-right: none;
  display: flex;
  flex-direction: column;
  z-index: 2;
}
.logo {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px 0 10px;
}
/* 品牌 logo：图自带白底，所以容器也用白底，避免出现色块边界 */
.logo-img {
  width: 36px;
  height: 36px;
  border-radius: var(--r-md);
  object-fit: cover;
  background: #fff;
  flex: none;
}
.logo-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px 8px;
}
.logo-text {
  font-size: 17px;
  font-weight: 700;
  letter-spacing: 1px;
  color: var(--c-text-1);
  white-space: nowrap;
}
.side-menu {
  border: none;
  background: transparent;
  flex: 1;
  padding: 4px 10px;
  overflow-y: auto;
}
.side-menu .el-menu-item {
  height: auto;
  line-height: 1.25;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 9px 0 !important;
  margin: 2px 0;
  border-radius: var(--r-md);
  color: var(--c-text-2);
  font-size: 11px;
  transition: background-color 0.16s ease, color 0.16s ease;
}
.side-menu .el-menu-item i {
  color: var(--c-text-3);
  margin: 0 0 4px 0;
  font-size: 19px;
  width: auto;
}
  /* 侧栏改成透明后，hover / 选中底色要加深一档才看得出来 */
.side-menu .el-menu-item:hover {
  background: var(--c-primary-weak) !important;
  color: var(--c-primary-ink);
}
.side-menu .el-menu-item:hover i { color: var(--c-primary); }
.side-menu .el-menu-item.is-active {
  background: rgba(59, 130, 246, 0.18) !important;
  color: var(--c-primary-ink) !important;
  font-weight: 600;
}
.side-menu .el-menu-item.is-active i { color: var(--c-primary); }

/* 侧栏底部：用户 / 登录 */

/* ------- 顶栏 ------- */
.app-header {
  height: 60px;
  /* 透明、无下边框：不再像单独的一行，而是和内容区连成一体 */
  background: transparent;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
}
.header-left { display: flex; align-items: center; gap: 10px; }
.header-badge {
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--c-primary);
}
.header-title {
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.5px;
  color: var(--c-text-1);
}
.user-bar { cursor: pointer; }
/* 触发区：悬停即展开，所以给一块可悬停的圆角热区，并抵消 padding 避免影响布局 */
.user-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 9px;
  margin: -4px -9px;
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: background 0.18s ease;
}
.user-name:hover { background: var(--c-primary-weak); }
.avatar-sm {
  width: 30px; height: 30px;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 600; color: #fff;
  border-radius: 50%;
  background: var(--c-primary);
}
.uname { color: var(--c-text-1); font-size: 14px; font-weight: 500; }

/* ===== 右上角用户弹层：自定义结构（el-popover）=====
   不用 el-dropdown：它把结构锁死成 ul/li，头部卡片只能靠 <li> 硬塞，
   分组线还要跟 Element 自带的 border-top / :before 打架。
   popover 里是任意结构，样式完全可控。
   深色主题：头部用饱和深海蓝做视觉焦点，其余全部走主题变量。 */
.user-pop.el-popover {
  padding: 0;
  border: none;
  border-radius: 20px;
  background: transparent;
  box-shadow:
    0 0 0 1px rgba(148, 163, 184, 0.12),
    0 4px 12px rgba(0, 0, 0, 0.45),
    0 26px 60px rgba(0, 0, 0, 0.55);
  overflow: hidden;
}
/* popover 默认带小箭头，这张卡片不需要 */
.user-pop .popper__arrow { display: none; }

/* ---- 头部用户卡：饱和深海蓝渐变 ---- */
.up-head {
  position: relative;
  padding: 20px;
  color: #fff;
  overflow: hidden;
  background:
    radial-gradient(118% 138% at 86% -6%, rgba(255, 255, 255, 0.22), rgba(255, 255, 255, 0) 54%),
    linear-gradient(135deg, #1e3a8a 0%, #2563eb 52%, #3b82f6 100%);
}
/* 右下角压一层暗光：纯色块有体积感，不是一片平 */
.up-head::after {
  content: '';
  position: absolute;
  right: -42px;
  bottom: -58px;
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0, 0, 0, 0.22), transparent 68%);
  pointer-events: none;
}
.up-id {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}
/* 头像：玻璃圆角方块（比纯色圆更有质感） */
.up-ava {
  flex: none;
  width: 46px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  font-weight: 600;
  border-radius: 15px;
  background: rgba(255, 255, 255, 0.18);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.38),
    0 8px 18px rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(6px);
}
.up-name {
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.35);
}
/* 积分：大号数字当主角（等宽数字，变数时不跳动） */
.up-balance {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
  cursor: pointer;
}
.up-balance:hover {
  opacity: 0.85;
}
.up-balance i {
  align-self: center;
  font-size: 16px;
  color: var(--c-brand-accent);
}
.up-balance b {
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.6px;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.up-balance em {
  font-style: normal;
  font-size: 12px;
  letter-spacing: 0.5px;
  opacity: 0.85;
}

/* ---- 菜单项 ---- */
.up-list {
  padding: 10px 10px 12px;
  background: var(--c-surface);
}
.up-it {
  display: flex;
  align-items: center;
  gap: 13px;
  height: 48px;
  padding: 0 12px;
  margin: 2px 0;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14.5px;
  font-weight: 500;
  color: var(--c-text-2);
  transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}
/* 图标装进主色淡底圆角方块；悬停填充主色，比单纯变色反馈更强 */
.up-ic {
  flex: none;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  border-radius: 11px;
  background: var(--c-primary-weak);
  color: var(--c-primary-ink);
  transition: background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
}
.up-lb {
  flex: 1;
  min-width: 0;
}
.up-it:hover {
  background: var(--c-primary-weaker);
  color: var(--c-primary-ink);
  transform: translateX(2px);
}
.up-it:hover .up-ic {
  background: var(--c-primary);
  color: #fff;
  box-shadow: 0 6px 14px rgba(59, 130, 246, 0.45);
}

/* 分组：两端淡出的细线，比通栏边框轻 */
.up-dv {
  position: relative;
  margin-top: 11px;
}
.up-dv::before {
  content: '';
  position: absolute;
  left: 14px;
  right: 14px;
  top: -6px;
  height: 1px;
  background: linear-gradient(90deg,
    transparent, rgba(148, 163, 184, 0.22) 16%, rgba(148, 163, 184, 0.22) 84%, transparent);
}

/* 登出：危险色但克制，悬停才实心 */
.up-danger { color: var(--c-danger); }
.up-danger .up-ic {
  background: rgba(248, 113, 113, 0.14);
  color: var(--c-danger);
}
.up-danger:hover {
  background: rgba(248, 113, 113, 0.1);
  color: var(--c-danger);
}
.up-danger:hover .up-ic {
  background: var(--c-danger);
  color: #fff;
  box-shadow: 0 6px 14px rgba(248, 113, 113, 0.4);
}

/* ===== 聯繫客服彈窗（LINE 二維碼）=====
   el-dialog 用了 append-to-body，scoped 属性选择器匹配不到，
   所以样式统一用 .support-dialog 前缀隔离，写在非 scoped 块里。 */
.support-dialog {
  max-width: calc(100vw - 24px);
}
.support-dialog .el-dialog__header {
  padding: 22px 22px 6px;
}
.support-dialog .el-dialog__body {
  padding: 0 22px 22px;
}
.cs-body {
  text-align: center;
}
.cs-tip {
  margin: 0 0 14px;
  font-size: 13px;
  line-height: 1.7;
  color: var(--c-text-3);
}
/* 二維碼圖自帶白底：外面再包一層白卡 + 陰影，深色主題下也不會顯成一塊灰。
   整块就是連結（<a>），所以要把 a 的默认下划线/颜色都清掉，再给点按反馈。 */
.cs-qr {
  display: inline-flex;
  padding: 10px;
  border-radius: var(--r-md);
  background: #fff;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.42);
  cursor: pointer;
  text-decoration: none;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
.cs-qr:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.52);
}
.cs-qr:active {
  transform: translateY(0);
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.42);
}
.cs-qr img {
  display: block;
  width: 220px;
  height: 220px;
}
/* 底部提示（原先把 lin.ee 网址直接露出来，现在只提示「點擊或掃描」） */
.cs-hint {
  margin: 14px 0 0;
  font-size: 13px;
  color: var(--c-text-3);
}

/* ------- 内容区 ------- */
.app-main {
  background: transparent;
  color: var(--c-text-1);
  padding: 0px 20px;
  /* 顶栏透明且不动，让内容区自己滚动，避免文字从顶栏下方穿过 */
  overflow-y: auto;
}

/* ===== 以下 Element 组件外观 common.css 里没管，这里补上（浅色）===== */
/* 滑块：视频时长 / 竞品相似度 */
.el-slider__runway { background-color: var(--c-surface-3); }
.el-slider__bar { background-color: var(--c-primary); }
.el-slider__button-wrapper .el-slider__button { border-color: var(--c-primary); background-color: #fff; }
.el-slider__button-wrapper .el-slider__button:hover { border-color: var(--c-primary-hover); }
.el-slider__marks-text { color: var(--c-text-3); }
.el-slider__stop { background-color: var(--c-primary-line); }

/* 计数器：步进按钮 */
.el-input-number { border: 1px solid var(--c-border-2); border-radius: var(--r-sm); }
.el-input-number:hover { border-color: var(--c-primary-line); }
.el-input-number .el-input-number__decrease,
.el-input-number .el-input-number__increase {
  background-color: var(--c-surface-2);
  color: var(--c-text-2);
}
.el-input-number .el-input-number__decrease:hover,
.el-input-number .el-input-number__increase:hover {
  background-color: var(--c-primary-weak);
  color: var(--c-primary-ink);
}
.el-input-number .el-input-number__decrease.is-disabled,
.el-input-number .el-input-number__increase.is-disabled {
  color: var(--c-text-4);
  background-color: transparent;
}

/* 进度条 */
.el-progress-bar__inner { background-color: var(--c-primary); }

/* ===================== 响应式 / 移动端适配 ===================== */
/* 汉堡按钮 */
.menu-btn {
  display: flex; align-items: center; justify-content: center;
  width: 34px; height: 34px;
  margin-right: 2px;
  font-size: 18px; color: var(--c-text-2);
  background: var(--c-surface);
  border: 1px solid var(--c-border-2);
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: all 0.2s;
}
.menu-btn:hover {
  background: var(--c-primary-weak);
  color: var(--c-primary-ink);
  border-color: var(--c-primary-line);
}

/* 抽屉菜单：浅色 */
.app-drawer .el-drawer { background: var(--c-surface) !important; }
.app-drawer .el-drawer__header {
  color: var(--c-text-1) !important;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 0;
  padding: 16px 20px 10px;
  border-bottom: 1px solid var(--c-border);
}
.app-drawer .el-drawer__close-btn { color: var(--c-text-3) !important; font-size: 20px; }
.app-drawer .el-drawer__body { display: flex; flex-direction: column; }
.app-drawer .drawer-menu { flex: 1; overflow-y: auto; margin-top: 4px; }

/* 抽屉里仍是横排（文字在图标右侧） */
.app-drawer .side-menu .el-menu-item {
  flex-direction: row;
  justify-content: flex-start;
  height: 46px;
  line-height: 46px;
  padding: 0 16px !important;
  font-size: 14px;
}
.app-drawer .side-menu .el-menu-item i { margin: 0 10px 0 0; font-size: 17px; }

/* 抽屉底部登录入口 */

/* 小屏（手机 / 窄平板）：一切换堆叠 + 减小内边距 */
@media screen and (max-width: 768px) {
  .app-shell { height: auto; min-height: 100vh; overflow: visible; }
  .app-aside { display: none; }
  .app-header { height: 54px; padding: 0 12px; }
  .header-title { font-size: 15px; }
  .uname { display: none; }          /* 手机上隐藏用户名，只留头像 */
  .app-main { padding: 10px; }       /* 减小内容区边距 */
  .right-wrap { height: auto !important; }
  .app-main { overflow: visible !important; }

  /* element 行/列堆叠 */
  .el-row { margin-left: 0 !important; margin-right: 0 !important; }
  .el-col { width: 100% !important; float: none !important; display: block; margin-bottom: 12px; }

  /* 卡片 / 弹窗适配 */
  .el-dialog { width: 94% !important; margin-top: 5vh !important; max-width: none !important; }
  .el-card__header { font-size: 14px; }

  /* 工具栏换行（按钮/输入并排时允许换行） */
  .toolbar { flex-wrap: wrap; gap: 8px; }
  .toolbar .el-input, .toolbar .el-select, .toolbar > .el-input, .toolbar > .el-select { width: 100% !important; max-width: none; }
  .toolbar .el-button { margin-left: 0; }

  /* 表单标签竖排，节省宽度 */
  .el-form--label-left .el-form-item__label,
  .el-form-item__label { float: none !important; display: block; text-align: left; width: auto !important; }
  .el-form-item__content { margin-left: 0 !important; }

  /* 表格横向滚动 + 窄内边距 */
  .el-table { font-size: 12px; }
  .el-table .cell { padding: 0 4px; }
  .el-table__body-wrapper { overflow-x: auto !important; }

  /* 模式切换（工作台）占满 */
  .mode-panel { padding: 10px; }
}

/* 超小屏进一步收缩 */
@media screen and (max-width: 480px) {
  .app-main { padding: 8px; }
  .header-badge { display: none; }
  .avatar-sm { width: 28px; height: 28px; font-size: 13px; }
  .el-button { padding-left: 10px; padding-right: 10px; }
}
</style>
